// Sugalarm — last-3-hours mini chart. Ported from chart.jsx.

import type { Reading, Thresholds, Zone } from "../../lib/types";
import { zoneColor } from "../../lib/glucose";

interface Props {
  readings: Reading[];
  thresholds: Thresholds;
  currentZone: Zone;
  /** When set the SVG renders at exactly this px width. When omitted
   *  (the common case) the SVG is `width: 100%` and uses an internal
   *  viewBox so it scales fluidly with the parent. */
  width?: number;
  height?: number;
}

// Internal viewBox width — chosen wide enough to look smooth at any
// rendered size up to a few thousand px.
const VIEW_W = 360;

export function Sparkline({
  readings,
  thresholds,
  currentZone,
  width,
  height = 68,
}: Props) {
  if (readings.length < 2) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--ink-3)",
          fontSize: "var(--type-floor)",
        }}
      >
        Awaiting more readings
      </div>
    );
  }

  const xs = readings.map((r) => new Date(r.timestamp).getTime());
  const minT = xs[0]!;
  const maxT = xs[xs.length - 1]!;
  const minY = 40;
  const maxY = 280;
  // Math runs in viewBox coords (0..VIEW_W × 0..height); the SVG either
  // renders at `width` px when set or at 100% of its container.
  const W = VIEW_W;
  const xScale = (t: number) =>
    maxT === minT ? W / 2 : ((t - minT) / (maxT - minT)) * W;
  const yScale = (v: number) => height - ((v - minY) / (maxY - minY)) * height;

  const path = readings
    .map((r, i) => {
      const x = xScale(new Date(r.timestamp).getTime()).toFixed(1);
      const y = yScale(r.sgv).toFixed(1);
      return `${i === 0 ? "M" : "L"}${x} ${y}`;
    })
    .join(" ");

  const last = readings[readings.length - 1]!;
  const lowY = yScale(thresholds.target_low);
  const highY = yScale(thresholds.target_high);

  return (
    <svg
      width={width ?? "100%"}
      height={height}
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none"
      style={{ display: "block" }}
    >
      {/* in-range band */}
      <rect
        x="0"
        y={highY}
        width={W}
        height={lowY - highY}
        fill="var(--zone-ok-soft)"
      />
      <line
        x1="0"
        y1={lowY}
        x2={W}
        y2={lowY}
        stroke="var(--line)"
        strokeDasharray="2 4"
      />
      <line
        x1="0"
        y1={highY}
        x2={W}
        y2={highY}
        stroke="var(--line)"
        strokeDasharray="2 4"
      />
      <path
        d={path}
        fill="none"
        stroke="var(--ink-2)"
        strokeWidth={1.6}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle
        cx={xScale(new Date(last.timestamp).getTime())}
        cy={yScale(last.sgv)}
        r={3.5}
        fill={zoneColor(currentZone)}
        stroke="var(--bg)"
        strokeWidth={1.5}
      />
    </svg>
  );
}
